// Outlook add-in: appearance and install steps.
// Only the message is saved. Name and logo travel in the downloaded manifest.
import { Download, ExternalLink, Info, Loader2 } from 'lucide-react'
import { Collapse, Field, ImageDrop, Panel, Segmented } from './SettingsPrimitives'

function Step({ n, title, children, action }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        {n}
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-[13px] font-medium text-gray-800 dark:text-gray-200">{title}</p>
        {children && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{children}</p>}
      </div>
      {action}
    </li>
  )
}

export default function PluginSection({
  applyToPlugin, setApplyToPlugin, bodyText, setBodyText, plugin, setPlugin, onPluginLogo, uploadingLogo, portalName,
  onDownload, downloading, manifestStale, disabled, bindSpot,
}) {
  return (
    <div className="space-y-4">
      <Panel title="Appearance">
        <div {...bindSpot('plugin-mode')} className="inline-block">
          <Segmented
            ariaLabel="Add-in appearance"
            value={applyToPlugin ? 'match' : 'custom'}
            onChange={v => setApplyToPlugin(v === 'match')}
            disabled={disabled}
            options={[
              { value: 'match', label: 'Same as web app' },
              { value: 'custom', label: 'Custom' },
            ]}
          />
        </div>

        <Collapse open={!applyToPlugin}>
          <div className="space-y-4 pt-5">
            <div {...bindSpot('plugin-name')}>
              <Field label="Name" htmlFor="settings-plugin-name" count={plugin.app_name.length} max={100}>
                <input
                  id="settings-plugin-name"
                  type="text"
                  className="form-input"
                  value={plugin.app_name}
                  onChange={e => setPlugin('app_name', e.target.value)}
                  maxLength={100}
                  placeholder={portalName || 'Xtract'}
                  disabled={disabled}
                />
              </Field>
            </div>
            <div {...bindSpot('plugin-logo')}>
              <ImageDrop
                compact
                label="Logo"
                hint="Square PNG or SVG"
                url={plugin.logo_url}
                busy={uploadingLogo}
                disabled={disabled}
                onFile={onPluginLogo}
              />
            </div>
            <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Info className="mt-px h-3.5 w-3.5 shrink-0" />
              Name and logo go into the manifest when you download it. They aren&apos;t saved.
            </p>
            <div {...bindSpot('plugin-body')}>
              <Field
                label="Message"
                htmlFor="settings-plugin-body"
                count={bodyText.length}
                max={500}
                hint="Shown above the attachment list"
              >
                <textarea
                  id="settings-plugin-body"
                  rows={2}
                  maxLength={500}
                  className="form-input min-h-[4.5rem] resize-y"
                  value={bodyText}
                  onChange={e => setBodyText(e.target.value)}
                  placeholder="Select the email attachments to upload and extract."
                  disabled={disabled}
                />
              </Field>
            </div>
          </div>
        </Collapse>
      </Panel>

      <Panel title="Install in Outlook">
        <ol className="space-y-4">
          <Step
            n={1}
            title="Download the manifest"
            action={(
              <button
                type="button"
                onClick={onDownload}
                disabled={disabled || downloading}
                className="btn btn-primary btn-sm shrink-0 gap-1.5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Download
              </button>
            )}
          />
          <Step n={2} title="Upload it in the Microsoft 365 admin center">
            Integrated apps → Upload custom apps.{' '}
            <a
              href="https://admin.microsoft.com/#/Settings/IntegratedApps"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 font-medium text-navy-600 hover:underline dark:text-navy-300"
            >
              Open <ExternalLink className="h-3 w-3" />
            </a>
          </Step>
          <Step n={3} title="Choose who gets the add-in" />
        </ol>
        <Collapse open={manifestStale}>
          <p className="pt-4 text-xs text-amber-700 dark:text-amber-300">Save your changes first so the manifest includes them.</p>
        </Collapse>
      </Panel>
    </div>
  )
}
